/**
 * DEFINITIONS
 * A Recipe (service or otherwise) represents a set of segments, combined using boolean expressions to obtain some demographic to target. A Recipe is the same thing as an audience.
 * Example: recipeForPeople40To60WhoOwnDogs = AND(People ages 40-60, dog owners) <-- each argument to that AND is a segment
 * Recipes can be either STANDARD or CUSTOM.
 *    STANDARD recipes are created by MadHive, and provide users with a bundle of logically-combined segments to use in their own recipes. Users cannot edit these, or create their own.
 *    CUSTOM recipes are created by users, and can contain any combination of segments and STANDARD recipes.
 */
export var BooleanOperation;
(function (BooleanOperation) {
    BooleanOperation["AND"] = "AND";
    BooleanOperation["OR"] = "OR";
    BooleanOperation["NOT"] = "NOT";
    BooleanOperation["IDENTITY"] = "IDENTITY"; // expresses just a segment as the entire expression
})(BooleanOperation || (BooleanOperation = {}));
export const booleanExpressionLegacyToBooleanExpression = (ex) => {
    const children = [];
    for (const child of ex.children || []) {
        children.push(booleanExpressionLegacyToBooleanExpression(child));
    }
    return {
        operation: ex.Op,
        ids: ex.ids,
        children
    };
};
export var RecipeType;
(function (RecipeType) {
    RecipeType["STANDARD"] = "STANDARD";
    RecipeType["CUSTOM"] = "CUSTOM";
    RecipeType["PREFERRED"] = "PREFERRED";
    RecipeType["UNKNOWN"] = "UNKNOWN";
})(RecipeType || (RecipeType = {}));
export var DerivedRecipeStatus;
(function (DerivedRecipeStatus) {
    DerivedRecipeStatus["READY"] = "READY";
    DerivedRecipeStatus["PARTIALLY_EXPIRED"] = "PARTIALLY_EXPIRED";
    DerivedRecipeStatus["EXPIRED"] = "EXPIRED";
    DerivedRecipeStatus["ARCHIVED"] = "ARCHIVED";
    DerivedRecipeStatus["UNKNOWN"] = "UNKNOWN";
})(DerivedRecipeStatus || (DerivedRecipeStatus = {}));
/**
 * @param expr: a boolean expression obtained from the frontend/adhering to BooleanExpression.
 * @return: an equivalent object, formatted to the ServiceBooleanExpression interface.
 */
export const booleanExpressionToServiceBooleanExpression = (expr) => {
    if (!expr) {
        return undefined;
    }
    // An expression node with one child that does not use the 'NOT' operator is superfluous
    if (!expr.ids.length && expr.children.length === 1 && expr.operation !== BooleanOperation.NOT) {
        return booleanExpressionToServiceBooleanExpression(expr.children[0]);
    }
    const isIdentityNode = expr.operation !== BooleanOperation.NOT && !expr.children.length && expr.ids.length === 1;
    return {
        // [and, or, not] are meaningless as operators if isIdentityNode is true
        operation: isIdentityNode ? BooleanOperation.IDENTITY : expr.operation,
        id: expr.ids,
        children: expr.children
            .map((childExpr) => booleanExpressionToServiceBooleanExpression(childExpr))
            .filter((child) => child !== undefined)
    };
};
/**
 * @param expr: a boolean expression obtained from the backend/adhering to ServiceBooleanExpression.
 * @return: an equivalent object, formatted to the BooleanExpression interface.
 */
export const serviceBooleanExpressionToBooleanExpression = (expr) => {
    // Handle case where expression is not loaded (e.g., loadBoolExpr=false)
    if (!expr) {
        return {
            operation: BooleanOperation.IDENTITY,
            ids: [],
            children: []
        };
    }
    return {
        operation: expr.operation,
        ids: expr.id,
        children: expr.children.map((childExpr) => serviceBooleanExpressionToBooleanExpression(childExpr))
    };
};
/**
 * Converts a client recipe to a service recipe.
 * Uses conditional spreading to only include fields that are actually present.
 * This allows for both full recipe conversion and partial updates.
 * @param recipe: the recipe to convert (can be partial or full)
 * @return: service recipe payload with only provided fields
 */
export const recipeToServiceRecipeBase = (recipe) => {
    return {
        ...("id" in recipe && { id: recipe.id }),
        ...("name" in recipe && { name: recipe.name }),
        ...("description" in recipe && { description: recipe.description }),
        ...("status" in recipe && { status: recipe.status }),
        ...("category" in recipe && { category: recipe.category }),
        ...("type" in recipe && { type: recipe.type }),
        ...("parent" in recipe && { parent: recipe.parent }),
        ...("preferred" in recipe && { preferred: !!recipe.preferred }),
        // Expression - requires transformation
        ...("expression" in recipe && {
            expr: booleanExpressionToServiceBooleanExpression(recipe.expression)
        }),
        // CPM fields
        ...("cpm" in recipe && {
            cpm: recipe.cpm?.max,
            min_cpm: recipe.cpm?.min
        }),
        // Read-only/computed fields
        ...("householdReach" in recipe && { household_reach: recipe.householdReach }),
        ...("derivedStatus" in recipe && { derived_status: recipe.derivedStatus }),
        ...("segmentIds" in recipe && { segment_ids: recipe.segmentIds })
    };
};
/**
 * @param recipe: a recipe obtained from the frontend/adhering to Recipe.
 * @return: an equivalent object, formatted to the ServiceRecipe interface.
 */
export const recipeToServiceRecipe = (recipe) => {
    return {
        id: recipe.id,
        name: recipe.name,
        type: recipe.type,
        parent: recipe.parent,
        category: recipe.category,
        cpm: recipe.cpm?.max,
        min_cpm: recipe.cpm?.min,
        description: recipe.description,
        household_reach: recipe.householdReach,
        expr: booleanExpressionToServiceBooleanExpression(recipe.expression),
        status: recipe.status,
        created_at: recipe.createdOn.getTime(),
        created_by: recipe.createdBy,
        preferred: !!recipe.preferred,
        derived_status: recipe.derivedStatus,
        segment_ids: recipe.segmentIds,
        // Note: segment_count is read-only and should not be sent in write requests
        last_modified: recipe.lastModified?.toISOString(),
        last_refreshed: recipe.lastRefreshed?.toISOString()
    };
};
/**
 * Validates and converts a string to a DerivedRecipeStatus enum value.
 * Returns undefined if the input is undefined, or DerivedRecipeStatus.UNKNOWN for invalid values.
 * @param status - The status string to validate
 * @returns A valid DerivedRecipeStatus enum value or undefined
 */
const parseDerivedRecipeStatus = (status) => {
    if (!status) {
        return undefined;
    }
    // Check if the status is a valid enum value
    if (Object.values(DerivedRecipeStatus).includes(status)) {
        return status;
    }
    // Default to UNKNOWN for invalid values
    return DerivedRecipeStatus.UNKNOWN;
};
/**
 * Safely parses a date string, returning null for invalid or empty values.
 * @param dateString - The date string to parse (RFC3339 format expected)
 * @returns A Date object if valid, null otherwise
 */
const parseDateString = (dateString) => {
    if (!dateString)
        return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
};
/**
 * @param recipe: a recipe obtained from the frontend/adhering to Recipe.
 * @return: an equivalent object, formatted to the ServiceRecipe interface.
 */
export const serviceRecipeToRecipe = (recipe) => {
    return {
        id: recipe.id,
        name: recipe.name,
        type: recipe.type,
        parent: recipe.parent,
        category: recipe.category,
        cpm: {
            min: recipe.min_cpm,
            max: recipe.cpm
        },
        get price() {
            // cpm === cost per mile === cost per 1,000 impressions
            // so, for the client's interpretation of price, we divide it
            return {
                min: recipe.min_cpm / 100,
                max: recipe.cpm / 100
            };
        },
        description: recipe.description,
        householdReach: recipe.household_reach,
        expression: serviceBooleanExpressionToBooleanExpression(recipe.expr),
        status: recipe.status,
        createdOn: new Date(recipe.created_at * 1000),
        createdBy: recipe.created_by,
        preferred: !!recipe.preferred,
        derivedStatus: parseDerivedRecipeStatus(recipe.derived_status),
        segmentIds: recipe.segment_ids,
        segmentCount: recipe.segment_count,
        lastModified: parseDateString(recipe.last_modified),
        lastRefreshed: parseDateString(recipe.last_refreshed)
    };
};
/**
 * @param orgRecipePreference: an org's recipe preference obtained from the frontend/adhering to OrgRecipePreference.
 * @return: an equivalent object, formatted to the ServiceOrgRecipePreference interface.
 */
export const orgRecipePreferenceToServiceOrgRecipePreference = (orgRecipePreference) => {
    return {
        org_id: orgRecipePreference.orgId,
        seg_id: orgRecipePreference.recipeId,
        preferred: !!orgRecipePreference.preferred
    };
};
/**
 * @param orgRecipePreference: an org's recipe preference obtained from the frontend/adhering to OrgRecipePreference.
 * @return: an equivalent object, formatted to the ServiceOrgRecipePreference interface.
 */
export const serviceOrgRecipePreferenceToOrgRecipePreference = (orgRecipePreference) => {
    return {
        orgId: orgRecipePreference.org_id,
        recipeId: orgRecipePreference.seg_id,
        preferred: !!orgRecipePreference.preferred
    };
};
//# sourceMappingURL=recipe.js.map